import {
	Avatar,
	Box,
	Button,
	Flex,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Text,
	useColorMode,
	useColorModeValue,
	useDisclosure
} from '@chakra-ui/react';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import AppearancePanel from 'components/settings/AppearancePanel';
import NotificationBell from 'features/admin-notifications/components/NotificationBell';
import CourtPulseWidget from 'features/court-pulse/CourtPulseWidget';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import { useState } from 'react';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import { MdPalette } from 'react-icons/md';
import { RType } from 'routes/types';
import { useAuth } from '../../features/auth/AuthProvider';
import ChangePasswordModal from '../../features/auth/ChangePasswordModal';
import { useUserRole } from '../../features/auth/roles';
import { useCoachTourStore } from '../../features/walkthrough/hooks/useCoachTourStore';
import { UserRole } from '../../models/organization/dto/invite-to-organization.dto';
import { translate } from '../../i18n';

const APPEARANCE_SEEN_KEY = 'appearance-panel-seen';

export default function NavbarLinks({ secondary, routes, enableFavorites, onCommandPaletteOpen }: { secondary: boolean; routes: RType[]; enableFavorites?: boolean; onOpen?: (...args: any[]) => any; fixed?: boolean; onCommandPaletteOpen?: () => void }) {
	const { colorMode, toggleColorMode } = useColorMode();
	const navbarIcon = useColorModeValue('gray.400', 'white');
	const menuBg = useColorModeValue('white', 'navy.800');
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
	const shadow = useColorModeValue(
		'14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
		'14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
	);
	const cmdPaletteBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
	const cmdPaletteBorder = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
	const cmdPaletteHoverBg = useColorModeValue('brand.50', 'whiteAlpha.200');
	const cmdPaletteHoverColor = useColorModeValue('brand.500', undefined);
	const cmdPaletteTextColor = useColorModeValue('secondaryGray.600', 'gray.400');
	// The account menu had exactly one item, so `_hover`/`_focus` being
	// suppressed cost nothing. With two it does: no hover target and, worse, no
	// visible focus ring when arrowing through the menu with a keyboard.
	const menuItemHoverBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
	const [appearanceSeen, setAppearanceSeen] = useState(
		() => localStorage.getItem(APPEARANCE_SEEN_KEY) === '1'
	);
	const { logout, currentUser } = useAuth();
	// MYS-132 — coaches can relaunch their onboarding tutorial from the account
	// menu; the layout-mounted CoachOnboardingTour picks the request up.
	const isCoach = useUserRole() === UserRole.COACH;
	const requestCoachTourReplay = useCoachTourStore.use.requestReplay();
	const {
		isOpen: isChangePasswordOpen,
		onOpen: onChangePasswordOpen,
		onClose: onChangePasswordClose
	} = useDisclosure();

	const displayName = (currentUser?.name as string) || (currentUser?.email as string) || '';
	const initials = displayName
		? displayName.split(/[\s@]/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()
		: '?';

	return (
		<Flex
			w={{ sm: '100%', md: 'auto' }}
			alignItems='center'
			flexDirection='row'
			bg={menuBg}
			flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
			p='10px'
			borderRadius='14px'
			boxShadow={shadow}>
			<SearchBar
				mb={() => {
					if (secondary) {
						return { base: '10px', md: 'unset' };
					}
					return 'unset';
				}}
				me='10px'
				borderRadius='12px'
			/>
			<SidebarResponsive routes={routes} enableFavorites={enableFavorites} />

			{/* Command palette trigger */}
			{onCommandPaletteOpen && (
				<Flex
					as="button"
					align="center"
					bg={cmdPaletteBg}
					border="1px solid"
					borderColor={cmdPaletteBorder}
					borderRadius="10px"
					px="8px"
					py="2px"
					me="6px"
					cursor="pointer"
					onClick={onCommandPaletteOpen}
					_hover={{
						bg: cmdPaletteHoverBg,
						color: cmdPaletteHoverColor,
					}}
				>
					<Text fontSize="xs" fontWeight="400" color={cmdPaletteTextColor}>
						{navigator.platform?.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl+K'}
					</Text>
				</Flex>
			)}

			{/* Appearance settings popover */}
			<Popover
				placement="bottom-end"
				isLazy
				onOpen={() => {
					if (!appearanceSeen) {
						localStorage.setItem(APPEARANCE_SEEN_KEY, '1');
						setAppearanceSeen(true);
					}
				}}
			>
				<PopoverTrigger>
					<Button
						variant='no-hover'
						bg='transparent'
						p='0px'
						minW='unset'
						minH='unset'
						h='18px'
						w='max-content'
						me='6px'
						position='relative'
					>
						<Icon
							h='18px'
							w='18px'
							color={navbarIcon}
							as={MdPalette}
						/>
						{!appearanceSeen && (
							<Box
								position='absolute'
								top='-3px'
								right='-3px'
								w='8px'
								h='8px'
								borderRadius='full'
								border='1.5px solid'
								borderColor={menuBg}
							>
								{/* Pulsing ring */}
								<Box
									position='absolute'
									inset='-3px'
									borderRadius='full'
									bg='brand.400'
									opacity={0.4}
									sx={{
										animation: 'pulse-ring 2s ease-in-out infinite',
										'@keyframes pulse-ring': {
											'0%, 100%': { transform: 'scale(1)', opacity: 0.4 },
											'50%': { transform: 'scale(1.8)', opacity: 0 },
										},
									}}
								/>
								{/* Solid dot */}
								<Box
									w='100%'
									h='100%'
									bg='brand.400'
									borderRadius='full'
								/>
							</Box>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					bg={menuBg}
					borderRadius='14px'
					boxShadow={shadow}
					border='none'
					w='auto'
				>
					<PopoverBody p='0'>
						<AppearancePanel />
					</PopoverBody>
				</PopoverContent>
			</Popover>

			{/* Dark mode toggle */}
			<Button
				variant='no-hover'
				bg='transparent'
				p='0px'
				minW='unset'
				minH='unset'
				h='18px'
				w='max-content'
				onClick={toggleColorMode}>
				<Icon
					me='10px'
					h='18px'
					w='18px'
					color={navbarIcon}
					as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
				/>
			</Button>

			{/* Court Pulse — real-time venue occupancy */}
			<CourtPulseWidget size={36} />

			{/* Notification bell */}
			<NotificationBell />

			<Menu>
				<MenuButton p='0px' data-tour='navbar-avatar'>
					<Avatar
						_hover={{ cursor: 'pointer' }}
						color='white'
						name={initials}
						bg='brand.500'
						size='sm'
						w='40px'
						h='40px'
					/>
				</MenuButton>
				<MenuList boxShadow={shadow} p='0px' mt='10px' borderRadius='14px' bg={menuBg} border='none'>
					<Flex w='100%' mb='0px'>
						<Text
							ps='20px'
							pt='16px'
							pb='10px'
							w='100%'
							borderBottom='1px solid'
							borderColor={borderColor}
							fontSize='sm'
							fontWeight='700'
							color={textColor}>
							{displayName || translate('auth.logout')}
						</Text>
					</Flex>
					<Flex flexDirection='column' p='10px'>
						{isCoach && (
							<MenuItem
								_hover={{ bg: menuItemHoverBg }}
								_focus={{ bg: menuItemHoverBg }}
								color={textColor}
								borderRadius='8px'
								onClick={requestCoachTourReplay}
								px='14px'>
								<Text fontSize='sm'>{translate('walkthrough.replay_coach_tour')}</Text>
							</MenuItem>
						)}
						<MenuItem
							_hover={{ bg: menuItemHoverBg }}
							_focus={{ bg: menuItemHoverBg }}
							color={textColor}
							borderRadius='8px'
							onClick={onChangePasswordOpen}
							px='14px'>
							<Text fontSize='sm'>{translate('auth.change_password')}</Text>
						</MenuItem>
						<MenuItem
							_hover={{ bg: menuItemHoverBg }}
							_focus={{ bg: menuItemHoverBg }}
							color='red.400'
							borderRadius='8px'
							onClick={logout}
							px='14px'>
							<Text fontSize='sm'>{translate('auth.logout')}</Text>
						</MenuItem>
					</Flex>
				</MenuList>
			</Menu>

			<ChangePasswordModal isOpen={isChangePasswordOpen} onClose={onChangePasswordClose} />
		</Flex>
	);
}
